// @ts-nocheck
import React from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CustomThemeProvider, usePortal } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  getCustomEvents,
  getHasFootfallAttribution,
  getHasViewability,
  getMetricsAndKpisByType,
  getReportingStatsGroups
} from '@/store/selectors/reporting.selectors';
import { ScheduleReport } from '@/ts-modules/reporting/ReportDownload/ScheduleReport';
import { prepareTableHeaders } from '@/ts-modules/reporting/reportingExport.helpers';
import { DownloadSubHeaderMulti, DownloadSubHeaderSingle } from '../DownloadSubHeader';
import { ReportingDownloadHeader } from './Header';
import { ReportDownloadForm } from './ReportDownloadForm';
import ReportDownloadSectionProvider from './ReportDownloadSection.context';
import { ReportingDownloadSectionStyle } from './reportingDownloadSection.style';

import type { GeoDictionary } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';
import type { StatsType } from '@/ts-types/reporting.types';
import type { Columns, DataByType, ReportConfigInitialData } from './types';

type Props = {
  campaigns: CampaignItem[];
  cartography: Partial<GeoDictionary>[];
  initialData?: ReportConfigInitialData;
  onBackClick: () => void;
  showGTotal?: boolean;
  stats: Record<string, unknown>;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

function ReportDownloadSection({
  campaigns,
  cartography,
  initialData,
  onBackClick,
  showGTotal = false,
  stats,
  user,
  userRoles
}: Props) {
  const targetBody = usePortal('reportDownloadSection');
  const intl = useIntl();
  const statsGroups = useSelector(getReportingStatsGroups);
  const hasViewability = useSelector(getHasViewability);
  const metricsKpiByType = useSelector(getMetricsAndKpisByType);
  const hasFootfallAttribution = useSelector(getHasFootfallAttribution);
  const customEvents = useSelector(getCustomEvents);
  const dataByType: Record<StatsType, Record<string, unknown>[]> = {
    footfallAttribution: prepareTableHeaders({
      campaigns,
      cartography,
      customEvents,
      intl,
      metricsAndKpis: metricsKpiByType,
      showGTotal,
      stats,
      statsGroups: statsGroups.footfallAttribution,
      statsType: 'footfallAttribution',
      user,
      userRoles
    }),
    stats: prepareTableHeaders({
      campaigns,
      cartography,
      customEvents,
      intl,
      metricsAndKpis: metricsKpiByType,
      showGTotal,
      stats,
      statsGroups: statsGroups.stats,
      statsType: 'stats',
      user,
      userRoles
    }),
    viewability: prepareTableHeaders({
      campaigns,
      cartography,
      customEvents,
      intl,
      metricsAndKpis: metricsKpiByType,
      showGTotal,
      stats,
      statsGroups: statsGroups.viewability,
      statsType: 'viewability',
      user,
      userRoles
    })
  };

  const campaignsIds = campaigns.map((campaign: CampaignItem) => campaign.id);
  const isSingle = campaigns.length === 1;
  const dataByTypeNormalized = Object.entries(dataByType).reduce((acc, [key, value]) => {
    if (key === 'footfallAttribution') {
      return {
        ...acc,
        [key]: hasFootfallAttribution
          ? (value as Columns[]).filter(item => item.prop === 'sdate' || !item.filterType)
          : []
      };
    }
    if (key === 'viewability') {
      return {
        ...acc,
        [key]: hasViewability ? (value as Columns[]).filter(item => item.prop === 'sdate' || !item.filterType) : []
      };
    }
    if (key === 'stats') {
      return {
        ...acc,
        [key]: (value as Columns[]).filter(item => item.prop === 'sdate' || !item.filterType)
      };
    }
    return acc;
  }, {} as DataByType);

  const scheduleReportPermission = checkUserResourcePermissions('reportingSchedule', user, userRoles);

  const hasAttribution = campaigns[0]?.attributionSettings?.enabled;

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <ReportDownloadSectionProvider
        campaignIds={campaignsIds}
        dataByType={dataByTypeNormalized}
        defaultEnd={campaigns[0].endDate}
        defaultStart={campaigns[0].beginDate}
        hasAttribution={hasAttribution}
        initialData={initialData}
        statsGroups={statsGroups}
        userId={user.id!}
      >
        <ReportingDownloadSectionStyle className="reportingDownloadContainer">
          <ReportingDownloadHeader id="reportingDownloadHeader" onBackClick={onBackClick} />
          {isSingle ? <DownloadSubHeaderSingle /> : <DownloadSubHeaderMulti />}
          <section className="reportingDownloadForm">
            <ReportDownloadForm />
            {scheduleReportPermission && <ScheduleReport user={user} userRoles={userRoles} />}
          </section>
        </ReportingDownloadSectionStyle>
      </ReportDownloadSectionProvider>
    </CustomThemeProvider>,
    targetBody
  );
}

export default ReportDownloadSection;
